import * as React from 'react'
import {Box, Typography, Chip, Button, Collapse, Table, TableHead, TableBody, TableRow, TableCell} from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {sectionCardSx, headerCellSx, cellSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, MUTED, withOpacity} from '../../../../styles/foundation/colors'
import {date, int, kg} from '../format'
import EmptyState from '../components/EmptyState'

/** '{date}, HH:MM' — `date()` (format.js) for the calendar date, a plain
 * `toLocaleTimeString` for the clock time. `new Date(isoString)` is fine
 * here; the module ban is only on an argument-less `new Date()`. */
function formatWhen(iso) {
  const time = new Date(iso).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'})
  return `${date(iso)}, ${time}`
}

/**
 * The in-session merge log — every merge and every reversal, in one place,
 * so a merchant can see what happened to their catalogue and undo it.
 * `log` is `mapLog` from the shared store; `roll` is `rollUp(skus)`, read
 * for the mother's CURRENT combined ships/mpw (never recomputed here).
 */
export default function MappingHistory({log, roll, skus, onUndo}) {
  const [open, setOpen] = React.useState(false)
  const entries = log || []

  return (
    <Box sx={sectionCardSx}>
      <Button
        fullWidth
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        sx={{
          justifyContent: 'flex-start', textTransform: 'none', color: NAVY,
          fontFamily: FONT, py: 0.5, px: 0, '&:hover': {bgcolor: 'transparent'},
        }}
      >
        <HistoryIcon sx={{fontSize: 18, color: SUBTLE, mr: 1}} />
        <Typography component='span' sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.sm, color: NAVY, mr: 1}}>
          Mapping history
        </Typography>
        {/* '0.65rem' was below the micro-type floor; SIZE['2xs'] is the smallest allowed. */}
        <Chip size='small' label={int(entries.length)} sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs']}} />
        <Box sx={{ml: 'auto', display: 'flex', color: SUBTLE}}>
          {open ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
        </Box>
      </Button>

      <Collapse in={open}>
        <Box sx={{mt: 1.5}}>
          {entries.length === 0 ? (
            <EmptyState
              icon={HistoryIcon}
              title='No merges yet'
              body='Select two or more SKUs in the table above, or take one of the suggestions, and the merge will be logged here with an option to reverse it.'
            />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Merge</TableCell>
                  <TableCell sx={headerCellSx}>Who</TableCell>
                  <TableCell sx={headerCellSx}>When</TableCell>
                  <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Shipments rolled up</TableCell>
                  <TableCell sx={headerCellSx}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => {
                  const motherRoll = roll[entry.mother]
                  const stillTracked = (skus || []).some((s) => s.sku === entry.mother)

                  return (
                    <TableRow key={entry.id}>
                      <TableCell sx={cellSx}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap'}}>
                          <Typography component='span' sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE}}>
                            {entry.children.join(', ')}
                          </Typography>
                          <ArrowForwardIcon sx={{fontSize: 14, color: SUBTLE}} />
                          <Typography component='span' sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY}}>
                            {entry.mother}
                          </Typography>
                        </Box>
                        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25}}>
                          {entry.undone
                            ? `Reversed on ${formatWhen(entry.undone)} by ${entry.undoneBy}`
                            : motherRoll
                              ? `${entry.mother} now reads ${int(motherRoll.ships)} shipments at ${kg(motherRoll.mpw)}`
                              : stillTracked
                                ? `${entry.mother} has no shipments of its own yet`
                                : `${entry.mother} was later renamed`}
                        </Typography>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{entry.by}</Typography>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{formatWhen(entry.at)}</Typography>
                      </TableCell>
                      <TableCell sx={{...cellSx, textAlign: 'right'}}>
                        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>
                          {motherRoll ? int(motherRoll.ships) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {entry.undone ? (
                          <Chip size='small' label='Reversed' sx={{fontFamily: FONT, fontSize: SIZE['2xs'], bgcolor: withOpacity(MUTED, 0.14), color: SUBTLE}} />
                        ) : (
                          <Button size='small' sx={secondaryBtnSx} onClick={() => onUndo(entry)}>Unmerge</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}
